import type { Result } from '../../types/result';
import type Lead from '../../types/leadDelivery';
import type { Offering, OrganizationMarketing } from '../../types/offerings';
import { getApiConfig } from '../../services/api/utils';
import { uuid } from '../../shared/utils';
import { createServiceTrackedHeaders } from '../createServiceTrackedHeaders';
import { getLocationInference } from './getLocationInference';
import {
  computeDcsConsolidation,
  programDifference,
} from '../dcsConsolidation';
import { toResult } from '../mappers/resultsMapper';
import { DeliverySystemApiClient } from '@highereducation/delivery-system';
import {
  type OfferingPayload,
  OfferingsServiceApiClient,
} from '@highereducation/offerings';
import { OrganizationServiceApiClient } from '@highereducation/organizations';

export type FetchQualifiedResultsParams = {
  lead: Lead;
  publisherToken?: string;
  trackingContext?: {
    anonymousId?: string;
  };
  degree?: string;
  category?: string;
  subject?: string;
  presentationUrl?: string;
  forceLinkoutSchoolIds?: number[];
  schoolIds?: number[];
  excludeProgramIds?: number[];
  limit?: number;
  exactOnly?: boolean;
  qualified?: string[];
  unqualified?: string[];
};

export type LocationInference = {
  stateAbbr: string;
  countryCode: string;
};

type Qualifications = Array<{
  id: string;
}>;

export type QualifiedResultsResponse = {
  qualified: Array<{ id: string }>;
  unqualified: Array<{ id: string }>;
  exactResults: Result[];
  relatedResults: Result[];
  recommendedResults: Result[];
  spotlightResults: Result[];
  locationInference?: LocationInference;
};

/**
 * Orchestrates fetching qualified results using SDK clients:
 * 1. Delivery System - qualify the lead (skipped if qualified/unqualified already provided)
 * 2. Offerings Service - fetch qualified offerings (with IP-based location inference)
 * 3. Organization Service - fetch marketing data
 * Then applies DCS consolidation and maps offerings to Result[].
 */
export const fetchQualifiedResults = async (
  params: FetchQualifiedResultsParams,
): Promise<QualifiedResultsResponse> => {
  if (!params.lead) {
    throw new Error('Lead is required to fetch qualified results');
  }

  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const config = getApiConfig();
  const deliverySystemBaseUrl = config.deliverySystemApiUrl ?? '';
  const offeringsBaseUrl = config.offeringsSearchApiUrl ?? '';
  const organizationBaseUrl = config.organizationApiUrl ?? '';
  const mobiusApiUrl = config.mobiusApiUrl ?? '';
  const headers = createServiceTrackedHeaders(uuid());
  const defaultHeaders = {
    ...headers,
  };

  try {
    // Step 1: Qualify the lead (skip if pre-computed qualifications provided)
    let qualified: string[] = params.qualified || [];
    let unqualified: string[] = params.unqualified || [];
    let qualifiedResults: Qualifications = qualified.map((id) => ({
      id,
    }));
    let unqualifiedResults: Qualifications = unqualified.map((id) => ({
      id,
    }));

    if (!params.qualified && !params.unqualified) {
      const client = new DeliverySystemApiClient({
        defaultHeaders,
        baseUrl: deliverySystemBaseUrl,
      });

      const qualificationsResponse = await client.postLeadQualifications({
        lead: params.lead,
        trackingContext: params.trackingContext || {},
      });

      if (!qualificationsResponse) {
        throw new Error('Invalid qualifications response');
      }

      qualified =
        qualificationsResponse.qualified
          ?.map((q) => q.id)
          .filter((id): id is string => !!id) || [];
      unqualified =
        qualificationsResponse.unqualified
          ?.map((q) => q.id)
          .filter((id): id is string => !!id) || [];

      qualifiedResults = (qualificationsResponse.qualified || []).flatMap(
        (q) => (q.id ? [{ id: q.id }] : []),
      );
      unqualifiedResults = (qualificationsResponse.unqualified || []).flatMap(
        (q) => (q.id ? [{ id: q.id }] : []),
      );
    }

    // Step 2: Fetch qualified offerings
    const locationResponse = await getLocationInference();

    const inferredStateAbbr =
      locationResponse?.regionCode || params.lead.location?.state || 'CA';
    const inferredCountryCode = locationResponse?.countryCode || 'US';

    // Lead-level country/state take priority over IP inference
    const resolvedState = params.lead.location?.state || inferredStateAbbr;
    const resolvedCountry =
      params.lead.location?.country || inferredCountryCode;

    const offeringPayload: OfferingPayload = {
      state: resolvedState,
      degree: params.degree || '',
      category: params.category || '',
      subject: params.subject || '',
      qualified,
      unqualified,
      country: resolvedCountry,
      channel: 'web',
      media: 'organic',
      experience: 'voyager',
      presentationUrl: params.presentationUrl || '',
      startDate: params.lead.startDate || undefined,
      schoolIds: params.schoolIds,
      excludeProgramIds: params.excludeProgramIds,
      limit: params.limit,
    };

    if (
      params.forceLinkoutSchoolIds &&
      Array.isArray(params.forceLinkoutSchoolIds) &&
      params.forceLinkoutSchoolIds.length > 0
    ) {
      offeringPayload.forceAudience = {
        audience: 'branded',
        schoolIds: params.forceLinkoutSchoolIds,
      };
    }

    const offeringsServiceApiClient = new OfferingsServiceApiClient({
      baseUrl: offeringsBaseUrl,
      defaultHeaders,
    });

    const qualifiedResponse =
      await offeringsServiceApiClient.getQualifiedOfferings(
        offeringPayload,
        locationResponse?.ip ? locationResponse.ip : null,
      );

    if (!qualifiedResponse || !qualifiedResponse?.data) {
      throw new Error('No qualified offerings found');
    }

    // Cast SDK offerings to local Offering type (structurally compatible)
    const { exact, related, recommended, spotlight } =
      qualifiedResponse.data as {
        exact: Offering[];
        related: Offering[];
        recommended: Offering[];
        spotlight: Offering[];
      };

    // Step 3: Fetch organization marketing data
    const allOfferings = [...exact, ...related, ...recommended, ...spotlight];

    const uniqueSchoolIds = Array.from(
      new Set(
        allOfferings
          .map((offering) => parseInt(offering.organization.id))
          .filter((schoolId) => schoolId !== undefined && !isNaN(schoolId)),
      ),
    );

    let organizationMarketingDataMap: Record<number, OrganizationMarketing> =
      {};
    let organizationMarketingBySlugMap: Record<string, OrganizationMarketing> =
      {};

    if (uniqueSchoolIds.length > 0) {
      try {
        const organizationsClient = new OrganizationServiceApiClient({
          baseUrl: organizationBaseUrl,
          defaultHeaders,
        });

        const organizationMarketingData =
          await organizationsClient.postMarketingLookup(
            {
              schoolIds: uniqueSchoolIds,
              isPreview: false,
              version: 'latest',
            },
            userAgent || 'unknown',
          );

        const marketingOrgs =
          (organizationMarketingData as { data: OrganizationMarketing[] })
            ?.data || [];
        organizationMarketingDataMap = {};
        organizationMarketingBySlugMap = {};
        for (const curr of marketingOrgs) {
          if (typeof curr.organizationId === 'number') {
            organizationMarketingDataMap[curr.organizationId] = curr;
          }
          // INT marketing sometimes returns TCPA rows without
          // `organizationId`; keep them reachable via slug.
          if (curr.slug) {
            organizationMarketingBySlugMap[curr.slug] = curr;
          }
        }
      } catch (error) {
        console.warn(
          'Failed to fetch organization marketing data, continuing without it',
          error,
        );
      }
    }

    // Step 4: Apply DCS consolidation
    const additionalOfferings = computeDcsConsolidation({
      degree: params.degree,
      exactOfferings: exact,
      relatedOfferings: related,
    });

    const consolidatedExactOfferings = [...exact, ...additionalOfferings];

    const remainingRelatedOfferings = programDifference<Offering>(
      related,
      additionalOfferings,
    );

    // Step 5: Map to Result[]
    const results: QualifiedResultsResponse = {
      qualified: qualifiedResults,
      unqualified: unqualifiedResults,
      locationInference: {
        stateAbbr: inferredStateAbbr,
        countryCode: inferredCountryCode,
      },
      exactResults: toResult(
        consolidatedExactOfferings,
        organizationMarketingDataMap,
        params.degree,
        params.forceLinkoutSchoolIds,
        params.publisherToken,
        mobiusApiUrl,
        organizationMarketingBySlugMap,
      ),
      relatedResults: toResult(
        remainingRelatedOfferings,
        organizationMarketingDataMap,
        params.degree,
        params.forceLinkoutSchoolIds,
        params.publisherToken,
        mobiusApiUrl,
        organizationMarketingBySlugMap,
      ),
      recommendedResults: toResult(
        recommended,
        organizationMarketingDataMap,
        params.degree,
        params.forceLinkoutSchoolIds,
        params.publisherToken,
        mobiusApiUrl,
        organizationMarketingBySlugMap,
      ),
      spotlightResults: toResult(
        spotlight,
        organizationMarketingDataMap,
        params.degree,
        params.forceLinkoutSchoolIds,
        params.publisherToken,
        mobiusApiUrl,
        organizationMarketingBySlugMap,
      ),
    };

    return results;
  } catch (error) {
    console.error('Could not fetch qualified results', error);
    throw error;
  }
};
